import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { getConfig } from '../config/config';
import { translator } from './Translator';

/**
 * แจ้งเตือน License ใกล้หมดอายุ — เรียก API license ของ isb-api
 * ⚠️ ไม่ส่ง company — server หาบริษัทจาก JWT เอง (isb-api docs/API_DOCUMENTATION §10)
 */

//message ของ error ตอน login เมื่อ License หมดอายุ (HTTP 403) — ต้องตรงกับ LICENSE_EXPIRED_CODE ฝั่ง isb-api
export const LICENSE_EXPIRED_CODE = "LICENSE_EXPIRED";

//ช่วงแจ้งเตือนของ isb-api ใช้วาดแถบความคืบหน้าใน popup
export const LICENSE_MILESTONES = [30, 15, 7, 3, 1];

const baseUrl = () => getConfig("API_URL", "");

const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("x-access-token")}`,
});

/**
 * สถานะ License ของบริษัทผู้ใช้
 * คืน null เมื่อเรียกไม่สำเร็จ ⇒ แค่ไม่แสดงแจ้งเตือน หน้าจออื่นใช้งานได้ตามปกติ
 */
export const fetchLicenseStatus = async () => {
    try {
        const result = await axios.get(`${baseUrl()}license/status`, { headers: authHeader() });
        return result.data;
    } catch (error) {
        console.error("fetchLicenseStatus", error);
        return null;
    }
}

/** แจ้งขอต่ออายุ — คืน { alreadyRequested, status } · error ให้ผู้เรียกแสดงข้อความเอง */
export const requestLicenseRenew = async () => {
    const result = await axios.post(`${baseUrl()}license/renew-request`, null, { headers: authHeader() });
    return result.data;
}

/** แสดงแจ้งเตือนเมื่ออยู่ในช่วงแจ้งเตือน (30 วันสุดท้าย) หรือหมดอายุแล้ว */
export const shouldShowLicenseNotice = (status) => {
    if (!status) return false;
    const inNoticeWindow = status.milestone !== null && status.milestone !== undefined;
    return inNoticeWindow || status.isExpired === true;
}

/**
 * คีย์จำว่าผู้ใช้เห็น popup ของช่วงนี้แล้ว — แยกตามบริษัท/ผู้ใช้/รอบวันหมดอายุ/ช่วง
 * ต่ออายุแล้ววันหมดอายุเปลี่ยน ⇒ ได้คีย์ใหม่ popup กลับมาแสดงตามช่วงเอง
 */
export const licenseNoticeKey = (company, username, status) => {
    const stage = status.isExpired ? "expired" : status.milestone;
    return `licenseNotice:${company}:${username}:${status.expireDate}:${stage}`;
}

//localStorage ใช้ไม่ได้ (เช่น private mode) ⇒ ถือว่ายังไม่เคยเห็น popup ยังแสดงได้ตามปกติ
export const hasSeenLicenseNotice = (key) => {
    try {
        return localStorage.getItem(key) === "1";
    } catch (error) {
        return false;
    }
}

export const markLicenseNoticeSeen = (key) => {
    try {
        localStorage.setItem(key, "1");
    } catch (error) {
        console.warn("markLicenseNoticeSeen", error);
    }
}

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' และ 'YYYY-MM-DD HH:mm:ss' → 'DD/MM/YYYY HH:mm' */
export const formatLicenseDate = (value) => {
    if (!value) return "";
    const withTime = value.length > 10;
    const date = moment(value, withTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD", true);
    return date.isValid() ? date.format(withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY") : value;
}

/** แทนค่า {name} ในข้อความแปลภาษา */
export const fillLicenseText = (template, values = {}) => {
    return String(template || "").replace(/\{(\w+)\}/g, (match, key) => (values[key] !== undefined ? values[key] : match));
}

/** response ของ auth/login ที่ isb-api ปฏิเสธเพราะ License ของบริษัทหมดอายุ */
export const isLicenseExpiredLoginError = (response) => {
    return !!response && response.status === 403 && !!response.data && response.data.message === LICENSE_EXPIRED_CODE;
}

/** alert ตอนกดเข้าสู่ระบบแล้ว License ของบริษัทหมดอายุ */
export const showLicenseExpiredAlert = (translate = {}) => {
    return Swal.fire({
        icon: "warning",
        title: translator(translate.ISA_License_ExpiredLogin_Title, "License หมดอายุ"),
        text: translator(
            translate.ISA_License_ExpiredLogin_Message,
            "License ของบริษัทหมดอายุแล้ว ไม่สามารถเข้าสู่ระบบได้ กรุณาติดต่อฝ่ายขายเพื่อต่ออายุการใช้งาน"
        ),
        confirmButtonText: translator(translate.ISA_OK, "OK"),
    });
}
